import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppFooter from "../components/AppFooter";
import { auth } from "../config/config-firebase";
import useMerchantStores from "../hooks/useMerchantStores";
import {
  ArrowLeft, Check, Building2,
  Upload, Trash2,
} from "lucide-react";

const ORG_KEY = "ezwinds_organisation";

const getOrg = () => {
  try {
    return JSON.parse(
      localStorage.getItem(ORG_KEY) || "null"
    );
  } catch { return null; }
};

const saveOrg = (data) =>
  localStorage.setItem(
    ORG_KEY,
    JSON.stringify(data)
  );

export default function OrganisationPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uid, setUid] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUid(currentUser?.uid || localStorage.getItem("customer_id") || null);
    });
    return () => unsubscribe();
  }, []);

  const { loadingStores, isTeamMember, isTeamMemberActive } = useMerchantStores(uid);

  // Team members don't get the Organisation page — redirect silently (no
  // error messaging, as if the route doesn't exist) once we know the role.
  // A *disabled* team member is redirected to /login by the hook itself.
  useEffect(() => {
    if (!loadingStores && isTeamMember && isTeamMemberActive) {
      navigate("/user-settings", { replace: true });
    }
  }, [loadingStores, isTeamMember, isTeamMemberActive, navigate]);

  if (!loadingStores && isTeamMember && isTeamMemberActive) {
    return null;
  }

  const existing = getOrg();
  const isEdit = !!existing;

  const [orgName, setOrgName] = useState(
    existing?.orgName || ""
  );
  const [ownerName, setOwnerName] = useState(
    existing?.ownerName || ""
  );
  const [email, setEmail] = useState(
    existing?.email || ""
  );
  const [phone, setPhone] = useState(
    existing?.phone || ""
  );
  const [address, setAddress] = useState(
    existing?.address || ""
  );
  const [city, setCity] = useState(
    existing?.city || ""
  );
  const [state, setState] = useState(
    existing?.state || ""
  );
  const [zip, setZip] = useState(
    existing?.zip || ""
  );
  const [country, setCountry] = useState(
    existing?.country || ""
  );
  const [logoPreview, setLogoPreview] = useState(
    existing?.logo || null
  );
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const inputCls = `w-full bg-gray-100
    rounded-xl px-4 py-3 text-sm
    text-gray-900 border border-transparent
    outline-none focus:bg-white
    focus:border-blue-500
    focus:ring-1 focus:ring-blue-500
    transition-colors`;

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) =>
      setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    if (fileInputRef.current)
      fileInputRef.current.value = "";
  };

  const handleSave = () => {
    if (!orgName.trim()) {
      setError("Organisation name is required.");
      return;
    }
    if (!ownerName.trim()) {
      setError("Owner name is required.");
      return;
    }
    if (!email.trim()) {
      setError("Email address is required.");
      return;
    }
    if (!phone.trim()) {
      setError("Phone number is required.");
      return;
    }
    setError("");

    saveOrg({
      orgName:   orgName.trim(),
      ownerName: ownerName.trim(),
      email:     email.trim(),
      phone:     phone.trim(),
      address:   address.trim(),
      city:      city.trim(),
      state:     state.trim(),
      zip:       zip.trim(),
      country:   country.trim(),
      logo:      logoPreview || null,
      updatedAt: new Date().toISOString(),
    });

    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="min-h-screen bg-gray-100
      flex flex-col">

      {/* HEADER */}
      <div className="bg-white px-4 pt-5 pb-4
        border-b border-gray-100 shadow-sm
        flex items-center gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={() => navigate("/user-settings")}
          className="flex items-center gap-1.5
            text-sm text-gray-600 border
            border-gray-200 px-3 py-1.5
            rounded-full flex-shrink-0"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <div>
          <h1 className="text-xl font-extrabold
            text-gray-900">
            Organisation
          </h1>
          <p className="text-xs text-gray-500">
            {isEdit
              ? "Update your organisation details"
              : "Set up your organisation profile"}
          </p>
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 px-4 pt-5 pb-28
        max-w-lg w-full mx-auto">

        {/* Success toast */}
        {saved && (
          <div className="bg-green-50 border
            border-green-200 rounded-2xl px-4
            py-3 mb-4 flex items-center gap-2">
            <Check size={15}
              className="text-green-500
              flex-shrink-0" />
            <p className="text-sm font-semibold
              text-green-700">
              Organisation saved successfully!
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-xs text-red-500
            bg-red-50 border border-red-200
            rounded-xl px-3 py-2 mb-4">
            {error}
          </p>
        )}

        {/* LOGO SECTION */}
        <div className="bg-white rounded-2xl
          shadow-sm border border-gray-100
          p-5 mb-4">
          <p className="text-sm font-bold
            text-gray-900 mb-3">
            Organisation logo
          </p>
          <div className="flex items-center gap-4">
            {/* Logo preview / placeholder */}
            <div className="w-20 h-20 rounded-2xl
              bg-blue-50 border-2 border-dashed
              border-blue-200 flex items-center
              justify-center flex-shrink-0
              overflow-hidden">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Organisation logo"
                  className="w-full h-full
                    object-cover"
                />
              ) : (
                <Building2 size={28}
                  className="text-blue-400" />
              )}
            </div>

            {/* Upload actions */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="org-logo-input"
                className="flex items-center
                  gap-1.5 bg-blue-600 text-white
                  text-xs font-bold px-3 py-2
                  rounded-full cursor-pointer"
              >
                <Upload size={12} />
                {logoPreview
                  ? "Change logo"
                  : "Upload logo"}
              </label>
              <input
                id="org-logo-input"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoChange}
              />
              {logoPreview && (
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="flex items-center
                    gap-1.5 bg-red-50 text-red-500
                    text-xs font-semibold px-3
                    py-2 rounded-full"
                >
                  <Trash2 size={12} /> Remove
                </button>
              )}
              <p className="text-[10px]
                text-gray-400">
                JPG, PNG up to 5MB
              </p>
            </div>
          </div>
        </div>

        {/* ORGANISATION DETAILS */}
        <div className="bg-white rounded-2xl
          shadow-sm border border-gray-100
          p-5 mb-4">
          <p className="text-sm font-bold
            text-gray-900 mb-4">
            Organisation details
          </p>

          <div className="space-y-3">

            {/* Organisation name */}
            <div>
              <label className="text-xs
                text-gray-600 font-medium
                mb-1 block">
                Organisation name
                <span className="text-red-400
                  ml-0.5">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Surya Foods Inc."
                value={orgName}
                onChange={(e) =>
                  setOrgName(e.target.value)}
                className={inputCls}
                autoFocus
              />
            </div>

            {/* Owner name */}
            <div>
              <label className="text-xs
                text-gray-600 font-medium
                mb-1 block">
                Owner / Contact name
                <span className="text-red-400
                  ml-0.5">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. John Smith"
                value={ownerName}
                onChange={(e) =>
                  setOwnerName(e.target.value)}
                className={inputCls}
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-xs
                text-gray-600 font-medium
                mb-1 block">
                Email address
                <span className="text-red-400
                  ml-0.5">*</span>
              </label>
              <input
                type="email"
                placeholder="hello@yourstore.com"
                value={email}
                onChange={(e) =>
                  setEmail(e.target.value)}
                className={inputCls}
              />
            </div>

            {/* Phone */}
            <div>
              <label className="text-xs
                text-gray-600 font-medium
                mb-1 block">
                Phone number
                <span className="text-red-400
                  ml-0.5">*</span>
              </label>
              <input
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value)}
                className={inputCls}
              />
            </div>

          </div>
        </div>

        {/* ADDRESS DETAILS */}
        <div className="bg-white rounded-2xl
          shadow-sm border border-gray-100
          p-5 mb-4">
          <p className="text-sm font-bold
            text-gray-900 mb-4">
            Address
          </p>

          <div className="space-y-3">

            {/* Street address */}
            <div>
              <label className="text-xs
                text-gray-600 font-medium
                mb-1 block">
                Street address
              </label>
              <input
                type="text"
                placeholder="123 Main Street"
                value={address}
                onChange={(e) =>
                  setAddress(e.target.value)}
                className={inputCls}
              />
            </div>

            {/* City + State row */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs
                  text-gray-600 font-medium
                  mb-1 block">
                  City
                </label>
                <input
                  type="text"
                  placeholder="Yonkers"
                  value={city}
                  onChange={(e) =>
                    setCity(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div className="w-24">
                <label className="text-xs
                  text-gray-600 font-medium
                  mb-1 block">
                  State
                </label>
                <input
                  type="text"
                  placeholder="NY"
                  value={state}
                  onChange={(e) =>
                    setState(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

            {/* ZIP + Country row */}
            <div className="flex gap-3">
              <div className="w-32">
                <label className="text-xs
                  text-gray-600 font-medium
                  mb-1 block">
                  ZIP / Postal code
                </label>
                <input
                  type="text"
                  placeholder="10701"
                  value={zip}
                  onChange={(e) =>
                    setZip(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div className="flex-1">
                <label className="text-xs
                  text-gray-600 font-medium
                  mb-1 block">
                  Country
                </label>
                <input
                  type="text"
                  placeholder="United States"
                  value={country}
                  onChange={(e) =>
                    setCountry(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

          </div>
        </div>

        {/* SAVE BUTTON */}
        <button
          type="button"
          onClick={handleSave}
          className="w-full bg-blue-600
            text-white font-bold text-sm
            py-4 rounded-full flex items-center
            justify-center gap-2"
        >
          <Check size={15} />
          {isEdit
            ? "Save changes"
            : "Save organisation"}
        </button>

      </div>

      <AppFooter />
    </div>
  );
}
